import { TAKE_BACK_BAG_URL } from '../config/externalURL';
import { ROUTE_MEMBERSHIP } from '../config/routes';
import {
  REWARDS_VALIDATED_STATE,
  REWARD_AMOUNT_TYPES,
  REWARD_TYPE,
} from '../config/rewards';
import {
  NOT_FOUND_ERROR,
  REWARDS_FUNDS_CODE_ERROR,
  REWARDS_NO_MORE_CODES,
  REWARDS_NO_MORE_CODES_PER_USER,
  REWARDS_LOCKED_CODE_ERROR,
} from './errors';
import CircleArrowRight from '../images/circle-arrow-right.svg';
import CircleArrowUp from '../images/circle-arrow-up.svg';
import CircleArrowDown from '../images/circle-arrow-down.svg';
import TCCoin from '../images/trashie-cash-v3.svg';
import { formatReadableNumber } from './numbers';

const getRewardCode = (rewardItem) => (
  rewardItem.type === REWARD_TYPE.VANITY || rewardItem.type === REWARD_TYPE.ONE_TIME ||
    rewardItem.type === REWARD_TYPE.SHOPIFY_DISCOUNT_CODE ||
    rewardItem.type === REWARD_TYPE.SEGMENTED_BY_PURCHASED_SKU
    ?
    (rewardItem.code) : null
);

const replaceRewardTags = (text, tags = {}) => {
  let newText = text;

  Object.entries(tags).forEach(([tag, value]) => {
    newText = newText.replace(new RegExp(`${tag}`), value);
  });

  return newText;
};

const isLimitErrorCode = (code) => code === REWARDS_NO_MORE_CODES;

const isUserLimitErrorCode = (code) => code === REWARDS_NO_MORE_CODES_PER_USER;

const isFundsErrorCode = (code) => code === REWARDS_FUNDS_CODE_ERROR;

const isLockedErrorCode = (code) => code === REWARDS_LOCKED_CODE_ERROR;

const isNotFoundErrorCode = (code) => code === NOT_FOUND_ERROR;

const getArrowImg = (isMobileSize, showDetails) => {
  if (!isMobileSize) {
    return CircleArrowRight;
  }
  if (isMobileSize && showDetails) {
    return CircleArrowUp;
  }
  return CircleArrowDown;
};

const getRewardItemValidated = (rewardItem, currentBalance = 0) => {
  const { exchangeValue, status } = rewardItem;

  if (!rewardItem || !status) {
    return {
      disabled: true,
    };
  }

  let disabled = false;
  let tooltipDisabled;
  let calcStatus;

  const insufficientFunds = parseFloat(currentBalance) < parseFloat(exchangeValue);

  if (
    isUserLimitErrorCode(status) ||
    isLimitErrorCode(status) ||
    insufficientFunds ||
    isLockedErrorCode(status)
  ) {
    disabled = true;
    tooltipDisabled = '';

    if (insufficientFunds) {
      calcStatus = REWARDS_FUNDS_CODE_ERROR;
    }
  }

  return {
    ...rewardItem,
    disabled,
    tooltipDisabled,
    ...(calcStatus && { status: calcStatus }),
  };
};

const getRewardsValidated = (rewards, currentBalance = 0) => {
  let areAllDisabled = true;

  const rewardsValidated = rewards.map(rewardItem => {
    const rewardItemValidated = getRewardItemValidated(rewardItem, currentBalance);

    if (areAllDisabled && !rewardItemValidated.disabled) {
      areAllDisabled = false;
    }

    return rewardItemValidated;
  }).sort((firstReward, secondReward) => {
    const {
      amountType: firstAmountType,
      rewardAmount: firstAmount,
    } = firstReward;
    const firstRewardAmountType = REWARD_AMOUNT_TYPES[firstAmountType];

    const {
      amountType: secondAmountType,
      rewardAmount: secondAmount,
    } = secondReward;
    const secondRewardAmountType = REWARD_AMOUNT_TYPES[secondAmountType];

    switch (true) {
      case
        (firstRewardAmountType && !firstRewardAmountType.hasAmount) ||
        (secondRewardAmountType && !secondRewardAmountType.hasAmount):
        return firstRewardAmountType.sortRanking - secondRewardAmountType.sortRanking;
      case
        (firstRewardAmountType && firstRewardAmountType.hasAmount) &&
        (secondRewardAmountType && secondRewardAmountType.hasAmount):
        return firstAmountType === secondAmountType ? (
          parseFloat(firstAmount) - parseFloat(secondAmount)
        ) : (
          firstRewardAmountType.sortRanking - secondRewardAmountType.sortRanking
        );
      default:
        return 0;
    }
  });

  return {
    rewardsValidated,
    validatedState: {
      [REWARDS_VALIDATED_STATE.NOT_ENOUGH_CASH]: areAllDisabled &&
        !rewardsValidated.some(({ status }) => !isFundsErrorCode(status)),
      [REWARDS_VALIDATED_STATE.SOLD_OUT]: areAllDisabled &&
        !rewardsValidated.some(({ status }) => !isLimitErrorCode(status)),
      [REWARDS_VALIDATED_STATE.USER_LIMIT]: areAllDisabled &&
        !rewardsValidated.some(({ status }) => !isUserLimitErrorCode(status)),
      [REWARDS_VALIDATED_STATE.ALL_DISABLED]: areAllDisabled,
    },
  };
};

const getRewardsSelectorTitle = (
  validatedState,
  rewardCount,
  isDeal,
  isGiveAway,
  hasActiveSubscription,
) => {
  const label = isDeal ? 'DEAL' : 'REWARD';

  const {
    [REWARDS_VALIDATED_STATE.NOT_ENOUGH_CASH]: notEnoughCash,
    [REWARDS_VALIDATED_STATE.SOLD_OUT]: soldOut,
    [REWARDS_VALIDATED_STATE.USER_LIMIT]: userLimit,
    [REWARDS_VALIDATED_STATE.ALL_DISABLED]: allDisabled,
  } = validatedState;

  switch (true) {
    case soldOut:
      return `${label} SOLD OUT`;
    case userLimit:
      return `${label} CLAIMED`;
    case notEnoughCash:
      return (
        <p>
          <a
            href={
            hasActiveSubscription ? ROUTE_MEMBERSHIP : TAKE_BACK_BAG_URL
          }
            style={{ textDecoration: 'underline' }}
          >
            GET MORE
          </a>
          {` TRASHIECASH™ TO UNLOCK THIS ${label}`}
        </p>
      );
    case allDisabled && isDeal:
      return `UNLOCK THIS ${label} WITH A TAKE BACK BAG`;
    case allDisabled && rewardCount > 1:
      return `NO ${label}S AVAILABLE`;
    case rewardCount === 1:
    case isGiveAway:
      return 'WHAT YOU’LL GET';
    default:
      return `CHOOSE YOUR ${label}`;
  }
};

const getRewardsAllStatus = (validatedState) => {
  const {
    [REWARDS_VALIDATED_STATE.NOT_ENOUGH_CASH]: notEnoughCash,
    [REWARDS_VALIDATED_STATE.SOLD_OUT]: soldOut,
    [REWARDS_VALIDATED_STATE.USER_LIMIT]: userLimit,
  } = validatedState;

  switch (true) {
    case notEnoughCash:
      return REWARDS_FUNDS_CODE_ERROR;
    case soldOut:
      return REWARDS_NO_MORE_CODES;
    case userLimit:
      return REWARDS_NO_MORE_CODES_PER_USER;
    default:
      return 'OK';
  }
};

const getRewardOptionSubtitle = (status, exchangeValue) => {
  switch (true) {
    case isLimitErrorCode(status):
      return 'SOLD OUT';
    case isUserLimitErrorCode(status):
      return 'CLAIMED';
    default:
      return exchangeValue > 0 ? `${formatReadableNumber(exchangeValue)}` : 'FREE!';
  }
};

const getRewardStatusSubtitle = (status) => {
  switch (true) {
    case isLimitErrorCode(status):
      return 'SOLD OUT';
    case isUserLimitErrorCode(status):
      return 'CLAIMED';
    case isFundsErrorCode(status):
      return (
        <>
          <span>You need more </span>
          <img src={TCCoin} alt="TCH" />
        </>
      );
    default:
      return '';
  }
};

const getUniqueLink = (rewardItem) => {
  const {
    affiliateLink,
    store: {
      url: storeUrl,
    } = {},
  } = rewardItem;

  return affiliateLink || storeUrl;
};

const isDonationType = type => (
  type === REWARD_TYPE.DONATION
);

const isGiveAwayType = type => (
  type === REWARD_TYPE.GIVEAWAY
);

const isRewardDeal = ({ exchangeValue = 0 }) => (
  Number(exchangeValue) === 0
);

function getTypeText({ isDonation, isDeal, isGiveAway }) {
  switch (true) {
    case isGiveAway:
      return 'GIVEAWAY';
    case isDonation:
      return 'DONATION';
    case isDeal:
      return 'DEAL';
    default:
      return 'REWARD';
  }
}

export {
  getRewardCode,
  replaceRewardTags,
  isLimitErrorCode,
  isUserLimitErrorCode,
  isFundsErrorCode,
  isLockedErrorCode,
  isNotFoundErrorCode,
  getArrowImg,
  getRewardsValidated,
  getRewardsSelectorTitle,
  getRewardsAllStatus,
  getRewardOptionSubtitle,
  getRewardStatusSubtitle,
  getUniqueLink,
  isDonationType,
  isGiveAwayType,
  isRewardDeal,
  getTypeText,
};
